import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, AlertCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

/**
 * Payment Terms selector — used inside the quote builder.
 *
 * The "Ox Recommended" preset varies by section (ProBound / ProEpoxy / ProDecor).
 * "Enter own" lets the installer add as many stages as they want (including a
 * single 100% "pay at the end" stage). Percentages must total 100% to be valid.
 *
 * Calls `onChange({preset, stages})` whenever the model changes; `onValidityChange`
 * flips true/false so the parent can gate Save & Send.
 */

export const OX_PRESETS_BY_SECTION = {
  probound: [
    { label: 'Deposit',                       percentage: 10 },
    { label: 'On project start',              percentage: 35 },
    { label: 'On groundworks completion',     percentage: 35 },
    { label: 'On project completion',         percentage: 20 },
  ],
  proepoxy: [
    { label: 'Deposit',                       percentage: 10 },
    { label: 'Project commencement',          percentage: 35 },
    { label: 'Design sign off',               percentage: 35 },
    { label: 'Project sign off',              percentage: 20 },
  ],
  prodecor: [
    { label: 'Deposit',                       percentage: 20 },
    { label: 'Project commencement',          percentage: 40 },
    { label: 'Project sign off',              percentage: 40 },
  ],
};

// Back-compat export (still ProBound by default).
export const OX_RECOMMENDED_STAGES = OX_PRESETS_BY_SECTION.probound;

const getOxPreset = (section) => {
  const key = (section || 'probound').toLowerCase();
  return OX_PRESETS_BY_SECTION[key] || OX_PRESETS_BY_SECTION.probound;
};

const formatPresetSummary = (stages) =>
  stages.map((s) => `${s.percentage}% ${s.label.toLowerCase()}`).join(' · ');

const CUSTOM_DEFAULT_STAGES = [
  { label: 'On project completion', percentage: 100 },
];

// Stable client-side id for each stage so React can track the row through
// reorders / adds / removes (avoids index-as-key state bleed).
const makeStageId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `pt_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const withStageIds = (arr) => arr.map((s) => ({ ...s, _uid: makeStageId() }));

export default function PaymentTermsSelector({ grandTotal = 0, value, onChange, onValidityChange, serviceType = 'probound' }) {
  const oxStages = useMemo(() => getOxPreset(serviceType), [serviceType]);
  const [preset, setPreset] = useState(value?.preset || 'ox_recommended');
  const [stages, setStages] = useState(
    value?.stages?.length
      ? withStageIds(value.stages.map((s) => ({ label: s.label || '', percentage: Number(s.percentage) || 0 })))
      : withStageIds(oxStages),
  );

  // Track the freshest `preset` and parent callbacks in refs so the sync/notify
  // effects below don't need them in their dep arrays — including them would
  // either cause redundant re-runs (preset) or infinite loops when the parent
  // hasn't wrapped `onChange`/`onValidityChange` in useCallback.
  const presetRef = useRef(preset);
  const onChangeRef = useRef(onChange);
  const onValidityChangeRef = useRef(onValidityChange);
  useEffect(() => { presetRef.current = preset; }, [preset]);
  useEffect(() => {
    onChangeRef.current = onChange;
    onValidityChangeRef.current = onValidityChange;
  });

  // Keep stages in sync if the section changes while user is on the Ox preset
  // (e.g. switching service_type in the Create Project form).
  useEffect(() => {
    if (presetRef.current === 'ox_recommended') setStages(withStageIds(oxStages));
  }, [oxStages]);

  // Sync preset → stages when switching radios
  const switchPreset = (next) => {
    setPreset(next);
    if (next === 'ox_recommended') setStages(withStageIds(oxStages));
    else setStages((cur) => (cur === oxStages ? withStageIds(CUSTOM_DEFAULT_STAGES) : cur));
  };

  const updateStage = (i, field, val) => {
    setStages((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, [field]: field === 'percentage' ? Number(val) || 0 : val } : s)),
    );
  };

  // Sum of every stage EXCEPT the one at index i — used to cap each slider so
  // the total can never exceed 100%.
  const sumOthers = (i) => stages.reduce((acc, s, idx) => (idx === i ? acc : acc + (Number(s.percentage) || 0)), 0);
  const totalPctNow = stages.reduce((acc, s) => acc + (Number(s.percentage) || 0), 0);
  const remainingPct = Math.max(0, Math.round((100 - totalPctNow) * 100) / 100);

  // New stage defaults to whatever % is still un-allocated. If 100% is already
  // assigned, it starts at 0 so the user can slide existing stages down to
  // make room.
  const addStage = () =>
    setStages((prev) => {
      const used = prev.reduce((acc, s) => acc + (Number(s.percentage) || 0), 0);
      const remaining = Math.max(0, Math.round((100 - used) * 100) / 100);
      return [...prev, { _uid: makeStageId(), label: '', percentage: remaining }];
    });
  const removeStage = (i) => setStages((prev) => prev.filter((_, idx) => idx !== i));

  const totalPct = stages.reduce((acc, s) => acc + (Number(s.percentage) || 0), 0);
  const isValid = Math.round(totalPct * 100) / 100 === 100 && stages.every((s) => s.label.trim());

  // Compute amounts on the fly from grand_total. _uid is kept on the row for
  // React key stability but stripped before notifying the parent.
  const stagesWithAmount = stages.map((s) => ({
    ...s,
    amount: +(grandTotal * (s.percentage / 100)).toFixed(2),
  }));

  // Notify parent on every change — strip _uid so it never leaks into persisted data.
  // Callbacks are read via refs to keep the effect stable even when the parent
  // re-renders with fresh (un-memoized) handler references.
  useEffect(() => {
    const cleanStages = stages.map(({ _uid, label, percentage }) => ({
      label,
      percentage,
      amount: +(grandTotal * (percentage / 100)).toFixed(2),
    }));
    const total = cleanStages.reduce((acc, s) => acc + (Number(s.percentage) || 0), 0);
    const valid = Math.round(total * 100) / 100 === 100 && cleanStages.every((s) => s.label.trim());
    onChangeRef.current?.({ preset, stages: cleanStages });
    onValidityChangeRef.current?.(valid);
  }, [preset, stages, grandTotal]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6" data-testid="payment-terms-card">
      <h3 className="text-base sm:text-lg font-bold text-[#27292E] mb-1">Payment Terms</h3>
      <p className="text-xs sm:text-sm text-gray-500 mb-4">How will the customer pay for this project?</p>

      {/* Radio toggle */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
        <button
          type="button"
          onClick={() => switchPreset('ox_recommended')}
          data-testid="pt-preset-ox"
          className={`p-3 sm:p-4 rounded-xl border-2 text-left transition-all ${
            preset === 'ox_recommended'
              ? 'border-[#006400] bg-[#006400]/5'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className={`w-4 h-4 ${preset === 'ox_recommended' ? 'text-[#006400]' : 'text-gray-400'}`} />
            <span className={`font-semibold text-sm ${preset === 'ox_recommended' ? 'text-[#006400]' : 'text-gray-700'}`}>
              Ox Recommended
            </span>
          </div>
          <p className="text-[11px] text-gray-500 leading-snug" data-testid="pt-preset-ox-summary">
            {formatPresetSummary(oxStages)}
          </p>
        </button>

        <button
          type="button"
          onClick={() => switchPreset('custom')}
          data-testid="pt-preset-custom"
          className={`p-3 sm:p-4 rounded-xl border-2 text-left transition-all ${
            preset === 'custom'
              ? 'border-[#006400] bg-[#006400]/5'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Plus className={`w-4 h-4 ${preset === 'custom' ? 'text-[#006400]' : 'text-gray-400'}`} />
            <span className={`font-semibold text-sm ${preset === 'custom' ? 'text-[#006400]' : 'text-gray-700'}`}>
              Enter own payment terms
            </span>
          </div>
          <p className="text-[11px] text-gray-500 leading-snug">
            Add as many stages as you like — or one single stage if paid on completion only.
          </p>
        </button>
      </div>

      {/* Stages — Ox preset shows compact rows; Custom uses wide name + slider */}
      {preset === 'ox_recommended' ? (
        <div className="space-y-2">
          {stagesWithAmount.map((stage, i) => (
            <div key={stage._uid} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0" data-testid={`pt-stage-${i}`}>
              <div className="flex-1 text-sm text-gray-700">{stage.label}</div>
              <div className="w-14 text-right font-semibold text-sm text-gray-900">{stage.percentage}%</div>
              <div className="w-28 text-right font-semibold text-sm text-gray-900">
                £{stage.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {stagesWithAmount.map((stage, i) => {
            const sliderMax = Math.min(100, Math.round((stage.percentage + (100 - totalPctNow)) * 100) / 100);
            return (
              <div
                key={stage._uid}
                className="rounded-xl border border-gray-200 p-3 sm:p-4 bg-gray-50/30"
                data-testid={`pt-stage-${i}`}
              >
                {/* Top row — wide name + amount + remove */}
                <div className="flex items-end gap-3">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor={`pt-label-${i}`} className="text-[10px] uppercase tracking-wider text-gray-500">Stage Name</Label>
                    <Input
                      id={`pt-label-${i}`}
                      data-testid={`pt-stage-${i}-label`}
                      value={stage.label}
                      onChange={(e) => updateStage(i, 'label', e.target.value)}
                      placeholder="e.g. Project start"
                      className="h-10 text-sm"
                    />
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Amount</div>
                    <div
                      className="h-10 flex items-center justify-end font-bold text-sm text-gray-900"
                      data-testid={`pt-stage-${i}-amount`}
                    >
                      £{stage.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  {stages.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStage(i)}
                      className="self-end h-10 px-2 text-rose-600 hover:bg-rose-50 rounded-md shrink-0"
                      data-testid={`pt-stage-${i}-remove`}
                      aria-label="Remove stage"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Slider row */}
                <div className="mt-3">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-gray-500">Percentage</Label>
                    <div className="text-sm font-bold text-[#006400]" data-testid={`pt-stage-${i}-pct-display`}>
                      {Number(stage.percentage).toFixed(2).replace(/\.00$/, '')}%
                    </div>
                  </div>
                  <Slider
                    data-testid={`pt-stage-${i}-slider`}
                    value={[Number(stage.percentage) || 0]}
                    onValueChange={(vals) => updateStage(i, 'percentage', vals[0])}
                    min={0}
                    max={sliderMax}
                    step={5}
                    className="w-full [&>span:first-child]:!bg-[#2F5E96]/25 [&>span:first-child>span]:!bg-[#2F5E96] [&_[role=slider]]:!border-[#2F5E96] [&_[role=slider]]:!bg-[#2F5E96]"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-gray-400">0%</span>
                    <span className="text-[10px] text-gray-400">max {Math.round(sliderMax)}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {preset === 'custom' && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <Button
            type="button"
            onClick={addStage}
            variant="outline"
            size="sm"
            className="h-9 text-xs"
            disabled={remainingPct <= 0}
            data-testid="pt-add-stage"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add stage
          </Button>
          <span
            className={`text-xs font-semibold ${remainingPct > 0 ? 'text-amber-700' : 'text-emerald-700'}`}
            data-testid="pt-remaining"
          >
            {remainingPct > 0
              ? `${remainingPct}% still to allocate`
              : 'All 100% allocated'}
          </span>
        </div>
      )}

      {/* Total + validity */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm">
        <div className={`flex items-center gap-2 ${isValid ? 'text-emerald-700' : 'text-rose-600'}`} data-testid="pt-total-pct">
          {!isValid && <AlertCircle className="w-4 h-4" />}
          <span className="font-semibold">Total: {totalPct.toFixed(2)}%</span>
          {!isValid && totalPct !== 100 && (
            <span className="text-[11px] text-gray-500">(must equal 100%)</span>
          )}
          {!isValid && totalPct === 100 && (
            <span className="text-[11px] text-gray-500">(every stage needs a label)</span>
          )}
        </div>
        <div className="text-gray-500 text-xs">
          Grand total: <strong className="text-gray-900">£{grandTotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
        </div>
      </div>
    </div>
  );
}
